import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    channelLabel, linkTypeLabel, MessageStatuses, Rights, kindLabel
} from '@aiinterview/shared';
import { api, ApiError } from '../api/client';
import { useSession } from '../api/session';
import { Button, Card, Select, Loading, toast } from './ui';
import { DataTable } from './DataTable';

/**
 * What has been sent, and what did not arrive.
 *
 * A campaign is not finished when the Send button stops spinning. Somebody has to come back
 * tomorrow and ask which twelve of the four hundred failed, and this is where they find out - the
 * summary on the send panel is gone by then.
 *
 * "Sent" here means the provider accepted it, which is not the same as delivered, and the column is
 * worded so nobody reads it as a receipt.
 */
interface Message {
    id: number;
    formatName: string;
    employeeCode: string | null;
    channel: string;
    linkType: string;
    recipient: string;
    status: string;
    attemptCount: number;
    lastError: string | null;
    sentAt: string | null;
    createdAt: string;
}

export function SentMessages({ kind }: { kind: 'Survey' | 'Listening' }) {
    const [status, setStatus] = useState('');
    const { can } = useSession();
    const queryClient = useQueryClient();

    const messages = useQuery({
        queryKey: ['interaction-messages', kind, status],
        queryFn: () => api.get<Message[]>(
            `/api/admin/interactions/messages?kind=${kind}${status ? `&status=${status}` : ''}`)
    });

    const retry = useMutation({
        mutationFn: (id: number) =>
            api.post<Message>(`/api/admin/interactions/messages/${id}/retry`),
        onSuccess: async (sent) => {
            await queryClient.invalidateQueries({ queryKey: ['interaction-messages', kind] });
            toast(
                sent.status === MessageStatuses.Sent ? 'Sent.' : 'It failed again.',
                sent.status === MessageStatuses.Sent ? 'success' : 'error');
        },
        onError: (caught: ApiError) => toast(caught.message, 'error')
    });

    const rows = messages.data ?? [];
    const failed = rows.filter((r) => r.status === MessageStatuses.Failed).length;

    return (
        <Card
            title="What has been sent"
            lede={failed > 0
                ? `${failed} did not go. Fix the address or the provider setting, then try again.`
                : `Every ${kindLabel(kind).toLowerCase()} link that has gone out.`}
            action={(
                <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-auto"
                >
                    <option value="">All of them</option>
                    <option value={MessageStatuses.Sent}>Sent</option>
                    <option value={MessageStatuses.Failed}>Did not go</option>
                </Select>
            )}
        >
            {messages.isLoading
                ? <Loading what="what has been sent" />
                : (
                    <DataTable
                        rows={rows}
                        rowKey={(row) => row.id}
                        empty="Nothing has been sent yet."
                        columns={[
                            {
                                header: 'Who',
                                sortBy: (row) => row.employeeCode ?? '',
                                cell: (row) => (
                                    <div>
                                        <div className="font-medium">{row.employeeCode || '—'}</div>
                                        <div className="text-xs text-slate-500">{row.recipient}</div>
                                    </div>
                                )
                            },
                            {
                                header: 'Which',
                                sortBy: (row) => row.formatName,
                                cell: (row) => (
                                    <div>
                                        <div>{row.formatName}</div>
                                        <div className="text-xs text-slate-500">
                                            {linkTypeLabel(row.linkType)}
                                        </div>
                                    </div>
                                )
                            },
                            {
                                header: 'How',
                                sortBy: (row) => row.channel,
                                cell: (row) => channelLabel(row.channel)
                            },
                            {
                                header: 'What happened',
                                sortBy: (row) => row.status,
                                cell: (row) => (
                                    <div>
                                        <div className={row.status === MessageStatuses.Sent
                                            ? 'font-medium text-emerald-700'
                                            : 'font-medium text-red-700'}
                                        >
                                            {row.status === MessageStatuses.Sent
                                                ? 'Handed to the provider'
                                                : 'Did not go'}
                                        </div>
                                        {/* The provider's own words. "Invalid template" and "not on
                                            WhatsApp" need different actions, and only it knows. */}
                                        {row.lastError && (
                                            <div className="text-xs text-slate-500">{row.lastError}</div>
                                        )}
                                    </div>
                                )
                            },
                            {
                                header: 'When',
                                sortBy: (row) => row.sentAt ?? row.createdAt,
                                cell: (row) => new Date(row.sentAt ?? row.createdAt)
                                    .toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                            },
                            {
                                header: '',
                                className: 'text-right',
                                cell: (row) => can(Rights.Create)
                                    && row.status === MessageStatuses.Failed && (
                                    <Button
                                        variant="secondary"
                                        busy={retry.isPending && retry.variables === row.id}
                                        onClick={() => retry.mutate(row.id)}
                                    >
                                        Try again
                                    </Button>
                                )
                            }
                        ]}
                    />
                )}
        </Card>
    );
}
